/**
 * System kontroli nawigacji - zapobiega ręcznym zmianom URL
 * i wymusza sekwencyjne przechodzenie przez proces podpisywania
 */

export type ProcessStep =
  | 'terms'
  | 'add-document'
  | 'qr-code'
  | 'summary'
  | 'waiting-for-signature'

export type ProcessState = {
  currentStep: ProcessStep
  allowedNextSteps: ProcessStep[]
  completedSteps: ProcessStep[]
}

// Definicja dozwolonych przejść między krokami procesu
const PROCESS_FLOW: Record<ProcessStep, ProcessStep[]> = {
  terms: ['add-document'],
  'add-document': ['qr-code'],
  'qr-code': ['summary'],
  summary: ['waiting-for-signature'],
  'waiting-for-signature': [], // Ostatni krok
}

// Mapowanie URL na kroki procesu
const URL_TO_STEP: Record<string, ProcessStep> = {
  '/': 'terms',
  '/add-document': 'add-document',
  '/qr-code': 'qr-code',
  '/summary': 'summary',
  '/waiting-for-signature': 'waiting-for-signature',
}

// Specjalne ścieżki dostępne zawsze (poza normalnym flow procesu)
export const URLS_ACCESSIBLE_FOR_STARTED_PROCESS = [
  '/session-expired',
  '/process-error',
]

const STEP_TO_URL: Record<ProcessStep, string> = {
  terms: '/',
  'add-document': '/add-document',
  'qr-code': '/qr-code',
  summary: '/summary',
  'waiting-for-signature': '/waiting-for-signature',
}

const SESSION_KEY = 'process_navigation_state'

export function getProcessState(): ProcessState {
  const stored = sessionStorage.getItem(SESSION_KEY)
  if (stored) {
    try {
      const state = JSON.parse(stored)
      // Sprawdź czy stan jest poprawny
      if (
        state.currentStep &&
        PROCESS_FLOW[state.currentStep as ProcessStep] !== undefined
      ) {
        return state
      }
    } catch {
      // Fallback jeśli dane są uszkodzone
    }
  }

  // Domyślny stan - rozpoczęcie od terms
  const defaultState: ProcessState = {
    currentStep: 'terms',
    allowedNextSteps: PROCESS_FLOW['terms'],
    completedSteps: [],
  }

  // Zapisz domyślny stan
  saveProcessState(defaultState)

  return defaultState
}

export function saveProcessState(state: ProcessState): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(state))
}

export function clearProcessState(): void {
  sessionStorage.removeItem(SESSION_KEY)
}

export function canAccessStep(targetStep: ProcessStep): boolean {
  const state = getProcessState()

  // Można zawsze wrócić do już ukończonych kroków
  if (state.completedSteps.includes(targetStep)) {
    return true
  }

  // Można przejść do aktualnego kroku
  if (state.currentStep === targetStep) {
    return true
  }

  // Można przejść tylko do dozwolonych następnych kroków
  return state.allowedNextSteps.includes(targetStep)
}

export function advanceToStep(targetStep: ProcessStep): boolean {
  const state = getProcessState()

  if (!canAccessStep(targetStep)) {
    return false
  }

  // Aktualizuj stan procesu
  const newCompletedSteps = [...state.completedSteps]
  if (!newCompletedSteps.includes(state.currentStep)) {
    newCompletedSteps.push(state.currentStep)
  }

  const newState: ProcessState = {
    currentStep: targetStep,
    allowedNextSteps: PROCESS_FLOW[targetStep] || [],
    completedSteps: newCompletedSteps,
  }

  saveProcessState(newState)
  return true
}

const is_process_started = () => {
  const stored = sessionStorage.getItem(SESSION_KEY)

  if (stored) {
    try {
      const state = JSON.parse(stored)
      return state.currentStep !== 'terms'
    } catch {
      // Fallback if data is corrupted
    }
  }

  return false
}

export function canAccessUrl(url: string): boolean {
  if (
    URLS_ACCESSIBLE_FOR_STARTED_PROCESS.includes(url) &&
    is_process_started()
  ) {
    return true
  }

  if (url.startsWith('/process-error')) {
    return true
  }

  const step = URL_TO_STEP[url]
  if (!step) {
    return false // Nieznany URL
  }

  return canAccessStep(step)
}

export function getStepFromUrl(url: string): ProcessStep | null {
  return URL_TO_STEP[url] ?? null
}

export function getUrlFromStep(step: ProcessStep): string {
  return STEP_TO_URL[step]
}

export function resetProcess(): void {
  clearProcessState()
  // Also clear all other session data
  sessionStorage.clear()
}
